import { useState } from 'react';
import style from './decline-curve-analysis-scenario-dropdown.module.scss';
import { ComboBox, TextInputSkeleton } from '@carbon/react';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import { Launch as LaunchIcon } from '@carbon/react/icons';
import { DcaScenariosResponse } from 'types/api-responses';

export type DeclineCurveAnalysisScenario = DcaScenariosResponse & {
  label: string;
};

interface DcaScenarioDropdownProps {
  scenarios: DeclineCurveAnalysisScenario[];
  selectedScenario: DeclineCurveAnalysisScenario | null;
  onScenarioSelect: (scenario: DeclineCurveAnalysisScenario | null) => void;
  onScenarioLaunch: (scenario: DeclineCurveAnalysisScenario) => void;
  title?: string;
  isLoading?: boolean;
  isError?: boolean;
}

export const DeclineCurveAnalysisScenarioDropdown = ({
  scenarios,
  selectedScenario,
  onScenarioSelect,
  onScenarioLaunch,
  title = 'DCA Scenarios',
  isLoading = false,
  isError = false,
}: DcaScenarioDropdownProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleScenarioChange = (event: any) => {
    // NOTE: this is also called when clicking on the Clear button, which is why there is no check `if (selectedScenario)`
    onScenarioSelect(event.selectedItem ?? null);

    // NOTE: if resetting the scenario, open the dropdown
    setIsDropdownOpen(!event.selectedItem);
  };

  // TODO: open dca scenario page
  const handleLaunchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedScenario) {
      onScenarioLaunch(selectedScenario);
    }
  };

  if (isLoading) {
    return (
      <div className={style.scenarioSelectorSkeleton}>
        <TextInputSkeleton
          hideLabel
          data-testid="dca-scenario-dropdown-skeleton"
        />
      </div>
    );
  }

  const dropdownLabel = (() => {
    if (isError) {
      return 'Error fetching scenarios';
    } else if (scenarios.length === 0) {
      return 'No scenarios available';
    } else if (!selectedScenario) {
      return 'Select a scenario';
    } else {
      return selectedScenario.name;
    }
  })();

  return (
    <div className={style.dropdownActionContainer}>
      <div className={style.comboBoxWrapper}>
        <ComboBox
          invalid={isError}
          titleText={title}
          size="md"
          id="dca-scenario-combobox"
          items={scenarios}
          itemToString={(item: DeclineCurveAnalysisScenario | null) =>
            item?.name || ''
          }
          placeholder={dropdownLabel}
          selectedItem={selectedScenario || null}
          // NOTE: `onChange(null)` is called when clicking Clear on the combobox
          onChange={handleScenarioChange}
          onToggleClick={() => setIsDropdownOpen((prev) => !prev)}
          onFocus={() => setIsDropdownOpen(true)}
          onBlur={() => setIsDropdownOpen(false)}
          shouldFilterItem={({ item, inputValue }) => {
            // Show all items when input is empty
            if (!inputValue || inputValue.trim() === '') return true;
            // Show all items when input matches selected item exactly (user just opened dropdown)
            if (selectedScenario && inputValue === selectedScenario.name)
              return true;
            // Filter when user is actively typing a different value
            return item.name.toLowerCase().includes(inputValue.toLowerCase());
          }}
          className={style.scenarioComboBox}
          data-testid="dca-scenario-combobox"
        />
      </div>
      {selectedScenario && !isDropdownOpen && (
        <div className={style.launchButtonWrapper}>
          <TooltipIcon
            data-testid="dca-scenario-launch"
            definition="Open scenario details"
            onClick={handleLaunchClick}
          >
            <LaunchIcon />
          </TooltipIcon>
        </div>
      )}
    </div>
  );
};
